import { CartSidebar } from "@/components/cart/CartSidebar";
import { ProductDetailDialogProvider } from "@/components/product/product-detail-dialog/ProductDetailDialogContext";
import { DrawerPanel } from "@/components/ui/drawer";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useReportOnce } from "@/hooks/useReportOnce";
import { analytics } from "@/lib/analytics";
import { PRODUCTS_INDEX, type TypesenseProduct } from "@/lib/typesense";
import {
  PREVIOUS_ORDERS_WINDOW_DAYS,
  usePreviousOrderProductIds
} from "@/queries/orders/previous-product-ids";
import { cn } from "@repo/design-system/utils";
import { Badge, Button, Heading, Input, Label } from "@medusajs/ui";

import { createRoute, useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  SlidersHorizontal,
  X
} from "lucide-react";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Configure,
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  usePagination,
  useRange,
  useRefinementList,
  useSearchBox,
  useSortBy,
  useToggleRefinement
} from "react-instantsearch";
import { layoutRoute } from "../$layoutRoute";
import { AppliedFilterChips, type AppliedChip } from "./AppliedFilterChips";
import type { SearchType } from "./models";
import {
  buildPreviousOrdersFilter,
  orderHitsByProductIds,
  previousOrdersFilterHasLanded
} from "./previous-orders";
import { SearchResults } from "./SearchResults";

/** One applied refinement, as reported by `useCurrentRefinements`. */
type CurrentRefinement = ReturnType<
  typeof useCurrentRefinements
>["items"][number]["refinements"][number];

// Search params interface
interface SearchParams {
  q?: string;
  type?: SearchType;
  brands?: string[];
  categories?: string[];
  suppliers?: string[];
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  sale?: boolean;
  /**
   * Previous-orders mode (KAN-51): results are the products this customer
   * ordered recently, rather than anything they typed. Carries no query of its
   * own - the id list comes from the order-history endpoint.
   */
  previous_orders?: boolean;
}

export const searchRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "search",
  validateSearch: (search?: Record<string, unknown>): SearchParams => {
    // Parse array params (can come as string or array)
    const parseArray = (value: unknown): string[] | undefined => {
      if (!value) return undefined;
      if (Array.isArray(value)) return value as string[];
      if (typeof value === "string") return [value];
      return undefined;
    };

    // Parse number params
    const parseNumber = (value: unknown): number | undefined => {
      if (value === undefined || value === null || value === "")
        return undefined;
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    };

    return {
      q: (search?.q as string) || undefined,
      type: search?.type ? (search.type as SearchType) : undefined,
      brands: parseArray(search?.brands),
      categories: parseArray(search?.categories),
      suppliers: parseArray(search?.suppliers),
      minPrice: parseNumber(search?.minPrice),
      maxPrice: parseNumber(search?.maxPrice),
      inStock:
        search?.inStock === true || search?.inStock === "true"
          ? true
          : undefined,
      sale: search?.sale === true || search?.sale === "true" ? true : undefined,
      previous_orders:
        search?.previous_orders === true || search?.previous_orders === "true"
          ? true
          : undefined
    };
  },
  component: SearchPage
});

/** The InstantSearch attribute strings the search page can filter on. */
type FacetAttribute =
  | "brand.name"
  | "categories.name"
  | "sellers.name"
  | "on_sale"
  | "in_stock"
  | "meta.min_price";

/**
 * One row of the facet table below. There are three shapes of facet on this
 * page and each needs slightly different wiring, so the descriptor is a
 * discriminated union on `kind`:
 *
 * - `list`   - a multi-select facet (brands / categories / suppliers) backed by
 *              a `?param=a&param=b` array and reachable via a `?type=` deep link.
 * - `toggle` - a boolean facet (on sale / in stock) backed by a single flag
 *              param, with a written chip label since the raw `on_sale: true`
 *              says nothing on its own.
 * - `range`  - the price filter, whose bound maps to `minPrice` or `maxPrice`
 *              depending on the applied refinement's operator.
 */
type FacetDescriptor =
  | {
      kind: "list";
      /** `?type=` value that deep-links into this facet (e.g. `?type=brand`). */
      typeValue: Exclude<SearchType, "product">;
      /** URL param holding the selected values (e.g. `?brands=A&brands=B`). */
      urlParam: "brands" | "categories" | "suppliers";
    }
  | {
      kind: "toggle";
      /** Boolean URL param this toggle drives. */
      urlParam: "sale" | "inStock";
      /** Written chip label - the raw `on_sale: true` isn't self-explanatory. */
      label: string;
    }
  | {
      kind: "range";
      /** URL params for the lower and upper bound, chosen by operator. */
      minParam: "minPrice";
      maxParam: "maxPrice";
    };

/**
 * The single source of truth for every filterable facet: attribute -> how it
 * maps to the `?type=` deep link, the URL param(s), the chip label, and the
 * chip's URL-removal patch. All the attribute-keyed behaviour below is derived
 * from this table, so adding a facet is one entry here - and because the table
 * is a `Record` over the whole `FacetAttribute` union, a missing entry is a
 * compile error rather than the silent URL-vs-refinement drift it used to be.
 */
const FACETS = {
  "brand.name": { kind: "list", typeValue: "brand", urlParam: "brands" },
  "categories.name": {
    kind: "list",
    typeValue: "category",
    urlParam: "categories"
  },
  "sellers.name": {
    kind: "list",
    typeValue: "supplier",
    urlParam: "suppliers"
  },
  on_sale: { kind: "toggle", urlParam: "sale", label: "On sale" },
  in_stock: { kind: "toggle", urlParam: "inStock", label: "In stock" },
  "meta.min_price": {
    kind: "range",
    minParam: "minPrice",
    maxParam: "maxPrice"
  }
  // `satisfies` (not a `: Record<...>` annotation) so each entry's literal
  // `kind` survives for `ListFacetAttribute` below, while still forcing every
  // `FacetAttribute` to have an entry.
} satisfies Record<FacetAttribute, FacetDescriptor>;

/**
 * The attributes whose facet is a multi-select list (brand / category /
 * supplier), derived from the table so it can't drift from it. `FilterSection`
 * renders a `useRefinementList` and is meaningful only for these - a toggle or
 * range attribute there would type-check but silently lose URL persistence
 * (`getListUrlParam` returns undefined for a non-list kind).
 */
type ListFacetAttribute = {
  [K in FacetAttribute]: (typeof FACETS)[K]["kind"] extends "list" ? K : never;
}[FacetAttribute];

/** The descriptor for an arbitrary attribute string, or undefined if unknown. */
function facetFor(attribute: string): FacetDescriptor | undefined {
  return FACETS[attribute as FacetAttribute];
}

// The attribute a `?type=` deep link filters on (e.g. `?type=brand&q=Dermacol`
// pre-selects the "Dermacol" brand). Derived from the list facets' `typeValue`.
function getAttributeForType(
  type: SearchType | undefined
): FacetAttribute | undefined {
  if (!type) return undefined;
  for (const attribute of Object.keys(FACETS) as FacetAttribute[]) {
    const descriptor = FACETS[attribute];
    if (descriptor.kind === "list" && descriptor.typeValue === type) {
      return attribute;
    }
  }
  return undefined;
}

// The URL array param a list facet's values live under (brands / categories /
// suppliers). Undefined for the toggle and range facets, which aren't arrays.
// The return type is derived from the table's list descriptor rather than
// re-listing the literals, so it can't drift from FACETS.
function getListUrlParam(
  attribute: string
): Extract<FacetDescriptor, { kind: "list" }>["urlParam"] | undefined {
  const descriptor = facetFor(attribute);
  return descriptor?.kind === "list" ? descriptor.urlParam : undefined;
}

// The chip label for one applied refinement, by facet kind. A list facet value
// (brand / category / supplier) speaks for itself; a toggle and the price bounds
// don't, so they get a written label rather than the raw `on_sale: true` / `>= 10`.
// Exhaustive over `kind` (like removeRefinementFromSearch) so a future facet
// shape that needs its own label is a compile error here, not a silently wrong
// raw-label render.
function formatRefinementLabel(refinement: CurrentRefinement): string {
  const descriptor = facetFor(refinement.attribute);
  if (!descriptor) return String(refinement.label);
  switch (descriptor.kind) {
    case "list":
      return String(refinement.label);
    case "toggle":
      return descriptor.label;
    case "range": {
      const op = refinement.operator;
      if (op === ">=" || op === ">") return `Min $${refinement.value}`;
      if (op === "<=" || op === "<") return `Max $${refinement.value}`;
      return `$${refinement.value}`;
    }
    default: {
      const _exhaustive: never = descriptor;
      return _exhaustive;
    }
  }
}

// The search-param patch that drops one applied refinement from the URL, so the
// always-mounted widgets' URL sync can't re-apply it when the panel reopens.
// Derived from the facet table: a list facet filters its value out of the array
// param (and clears `type`, matching the sidebar checkboxes); a toggle clears
// its flag; the price bounds clear `minPrice` or `maxPrice` by operator. The
// `default` is unreachable for the six mounted facets - it exists so that adding
// a new `kind` without handling it here is a compile error, not silent drift.
function removeRefinementFromSearch(
  refinement: CurrentRefinement,
  prev: SearchParams
): SearchParams {
  const descriptor = facetFor(refinement.attribute);
  if (!descriptor) return prev;
  switch (descriptor.kind) {
    case "list": {
      const remaining = (prev[descriptor.urlParam] ?? []).filter(
        (value) =>
          value.toLowerCase() !== String(refinement.value).toLowerCase()
      );
      return {
        ...prev,
        [descriptor.urlParam]: remaining.length > 0 ? remaining : undefined,
        type: undefined
      };
    }
    case "toggle":
      return { ...prev, [descriptor.urlParam]: undefined };
    case "range": {
      const op = refinement.operator;
      if (op === ">=" || op === ">")
        return { ...prev, [descriptor.minParam]: undefined };
      if (op === "<=" || op === "<")
        return { ...prev, [descriptor.maxParam]: undefined };
      return {
        ...prev,
        [descriptor.minParam]: undefined,
        [descriptor.maxParam]: undefined
      };
    }
    default: {
      const _exhaustive: never = descriptor;
      return _exhaustive;
    }
  }
}

function SearchPage() {
  const {
    q,
    type,
    brands,
    categories,
    suppliers,
    minPrice,
    maxPrice,
    inStock,
    sale,
    previous_orders: previousOrders
  } = searchRoute.useSearch();
  const navigate = useNavigate();
  const { refine: refineSearch } = useSearchBox();
  const { items, results } = useHits<TypesenseProduct>();

  // The desktop sidebar can be hidden; below `lg` the filters live in a drawer
  // instead. Two separate controls: `showFilters` toggles the persistent
  // sidebar (desktop only), `drawerOpen` opens the off-canvas panel (phone /
  // tablet). The drawer defaults closed, so the panel is open by default only
  // on desktop.
  const [showFilters, setShowFilters] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDesktop = useBreakpoint("lg");

  // The live applied refinements, for the chips above the results and the count
  // on the phone `Filters (n)` button. `refine` here removes a single one.
  const { items: appliedRefinements, refine: removeRefinement } =
    useCurrentRefinements();
  const { refine: clearRefinements } = useClearRefinements();

  // Previous-orders mode. The query only runs in this mode; everywhere else the
  // page never asks for the customer's history at all.
  const isPreviousOrders = Boolean(previousOrders);
  const {
    productIds: previousOrderIds,
    isPending: previousOrdersPending,
    isError: previousOrdersFailed,
    refetch: refetchPreviousOrders
  } = usePreviousOrderProductIds({ enabled: isPreviousOrders });
  const previousOrdersFilter = useMemo(
    () => buildPreviousOrdersFilter(previousOrderIds),
    [previousOrderIds]
  );

  // Three states that must not fall through to the grid, because each would
  // render the whole catalogue under a heading promising the opposite.
  //
  // Loading is NOT just "the ids request is in flight": the dropdown prefetches
  // the same query key on focus, so the ids are usually already cached and the
  // request never pends here. What matters is whether the results on screen
  // came from the id filter, which only the results can answer.
  //
  // The "has it landed?" check is gated on there being a filter at all: with an
  // empty history none is ever issued, so it could never become true and the
  // page would sit on "Loading" forever instead of reaching the empty state.
  const previousOrdersLoading =
    isPreviousOrders &&
    !previousOrdersFailed &&
    (previousOrdersPending ||
      (previousOrderIds.length > 0 &&
        !previousOrdersFilterHasLanded(results, items, previousOrderIds)));

  // A failed request is NOT an empty history. Telling a salon with orders that
  // they have none is a false claim about their own account, and with
  // `retry: false` a single blip is enough to trigger it.
  const previousOrdersEmpty =
    isPreviousOrders &&
    !previousOrdersPending &&
    !previousOrdersFailed &&
    previousOrderIds.length === 0;

  // Restore the endpoint's most-recently-ordered-first sequence, which Typesense
  // cannot produce (see ./previous-orders). Skipped when the sort dropdown has
  // switched to a price-sorted virtual index - the customer asked for that order
  // explicitly, and it must win over the mode's default.
  const displayItems = useMemo(() => {
    if (!isPreviousOrders || results?.index !== PRODUCTS_INDEX) return items;
    return orderHitsByProductIds(items, previousOrderIds);
  }, [isPreviousOrders, items, previousOrderIds, results?.index]);

  // Determine which attribute needs initial filtering from type param
  const initialFilterAttribute = getAttributeForType(type);
  const initialFilterValue = type && type !== "product" ? q : undefined;

  // The selected values for a list facet, read live from the URL. Which param
  // holds them comes from the facet table; the values themselves are the
  // current search params, so this stays a component-scoped callback.
  const getUrlValuesForAttribute = useCallback(
    (attribute: string): string[] | undefined => {
      const param = getListUrlParam(attribute);
      if (!param) return undefined;
      return { brands, categories, suppliers }[param];
    },
    [brands, categories, suppliers]
  );

  // Update URL when filters change
  const updateUrlFilters = useCallback(
    (attribute: string, values: string[]) => {
      const paramName = getListUrlParam(attribute);
      if (!paramName) return;

      navigate({
        to: "/search",
        search: (prev) => ({
          ...prev,
          [paramName]: values.length > 0 ? values : undefined,
          // Clear type param when manually changing filters
          type: undefined
        }),
        replace: true
      });
    },
    [navigate]
  );

  // Clear all filter URL params. Deliberately a FRESH object keeping only `q`
  // and the mode - not a `...prev` spread, and not derived from FACETS like the
  // per-chip removeRefinementFromSearch. It is still drift-safe for a facet added
  // later without being edited here: TanStack's functional `search` updater
  // REPLACES rather than merges (functionalUpdate returns the updater's result
  // verbatim), and validateSearch re-derives from that result via an allowlist -
  // so any param not named here is absent from the result and thus dropped. A new
  // facet can't silently survive "Clear all", and needs no entry in this list.
  const clearUrlFilters = useCallback(() => {
    navigate({
      to: "/search",
      search: (prev) => ({
        q: prev.q, // Keep the search query
        // Keep the mode too: clearing the sidebar filters means "show me all my
        // previous orders again", not "drop me into an unfiltered catalogue".
        previous_orders: prev.previous_orders,
        // Clear all filter params
        type: undefined,
        brands: undefined,
        categories: undefined,
        suppliers: undefined,
        minPrice: undefined,
        maxPrice: undefined,
        inStock: undefined,
        sale: undefined
      }),
      replace: true
    });
  }, [navigate]);

  // Clear the InstantSearch refinements and the URL together. Both halves
  // matter: the refinements drive the live results, the URL is what the
  // always-mounted filter widgets re-read.
  const clearAll = useCallback(() => {
    clearRefinements();
    clearUrlFilters();
  }, [clearRefinements, clearUrlFilters]);

  // Remove a single applied filter from a chip. It drops the refinement from
  // the live query at once and then rewrites the URL to match - if it only did
  // the former, reopening the panel would let the widgets' URL sync re-apply it.
  // The URL patch is derived from the facet table (see removeRefinementFromSearch).
  const handleRemoveRefinement = useCallback(
    (refinement: CurrentRefinement) => {
      removeRefinement(refinement);
      navigate({
        to: "/search",
        replace: true,
        search: (prev) => removeRefinementFromSearch(refinement, prev)
      });
    },
    [navigate, removeRefinement]
  );

  // A drawer left open while the viewport grows into desktop would sit over the
  // page next to the now-visible sidebar. Close it at the boundary; the drawer
  // is only rendered below `lg` anyway, so this also resets the state cleanly.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Update URL when price filter changes
  const updatePriceUrl = useCallback(
    (min: number | undefined, max: number | undefined) => {
      navigate({
        to: "/search",
        search: (prev) => ({
          ...prev,
          minPrice: min,
          maxPrice: max
        }),
        replace: true
      });
    },
    [navigate]
  );

  // Set initial search query from URL (only for product searches, not type filters)
  useEffect(() => {
    if (q && type === "product") {
      refineSearch(q);
    } else if (q && !type) {
      refineSearch(q);
    } else {
      // Type filter is applied, clear text search
      refineSearch("");
    }
  }, [q, type, refineSearch]);

  // Record that a free-text search's results settled, with the result count
  // (including 0 for zero-result searches). Type-based navigations are facet
  // browsing, not free-text search, so they are excluded.
  //
  // The query is the occurrence, so a new query reports and the same one does
  // not. Everything else - the results having caught up with the query, a facet
  // navigation, an empty search box - is readiness: it holds without re-arming,
  // so browsing away by type and back does not re-report a query already
  // counted. `results.nbHits` is read live inside the callback, so it is the
  // count that settled rather than whatever had loaded first.
  useReportOnce(
    q ?? null,
    (query) => {
      analytics.searchResultsViewed({
        query,
        results_count: results?.nbHits ?? 0
      });
    },
    Boolean(q) && !type && results?.query === q
  );

  // The applied filters, flattened to one removable chip each. The chips and
  // the phone `Filters (n)` count both read from this, so they never disagree.
  const chips: AppliedChip[] = appliedRefinements.flatMap((item) =>
    item.refinements.map((refinement) => ({
      // Operator is part of the identity so the two price bounds stay distinct
      // even when min and max happen to be the same number.
      key: `${refinement.attribute}:${refinement.operator ?? ""}:${refinement.value}`,
      label: formatRefinementLabel(refinement),
      onRemove: () => handleRemoveRefinement(refinement)
    }))
  );
  const appliedCount = chips.length;

  // The filter controls, written once and mounted in two places: the desktop
  // sidebar (always mounted - see the aside) and the phone/tablet drawer. React
  // instantiates each usage independently, so the two surfaces keep their own
  // expand/collapse state while sharing the one InstantSearch refinement state.
  const filterBody = (
    <>
      {/* On Sale Filter */}
      <OnSaleFilter
        urlValue={sale}
        onFilterChange={(value) =>
          navigate({
            to: "/search",
            search: (prev) => ({ ...prev, sale: value || undefined }),
            replace: true
          })
        }
      />

      {/* In Stock Filter */}
      <InStockFilter
        urlValue={inStock}
        onFilterChange={(value) =>
          navigate({
            to: "/search",
            search: (prev) => ({
              ...prev,
              inStock: value || undefined
            }),
            replace: true
          })
        }
      />

      {/* Categories Filter */}
      <FilterSection
        title="Categories"
        attribute="categories.name"
        initialValue={
          initialFilterAttribute === "categories.name"
            ? initialFilterValue
            : undefined
        }
        urlValues={getUrlValuesForAttribute("categories.name")}
        onFilterChange={updateUrlFilters}
      />

      {/* Brands Filter */}
      <FilterSection
        title="Brands"
        attribute="brand.name"
        initialValue={
          initialFilterAttribute === "brand.name"
            ? initialFilterValue
            : undefined
        }
        urlValues={getUrlValuesForAttribute("brand.name")}
        onFilterChange={updateUrlFilters}
      />

      {/* Sellers Filter */}
      <FilterSection
        title="Suppliers"
        attribute="sellers.name"
        initialValue={
          initialFilterAttribute === "sellers.name"
            ? initialFilterValue
            : undefined
        }
        urlValues={getUrlValuesForAttribute("sellers.name")}
        onFilterChange={updateUrlFilters}
      />

      {/* Price Range Filter */}
      <PriceRangeFilter
        urlMinPrice={minPrice}
        urlMaxPrice={maxPrice}
        onPriceChange={updatePriceUrl}
      />
    </>
  );

  return (
    <ProductDetailDialogProvider>
      {/* Exact id match, and the whole set in one page so the ordering below
          holds across it rather than only within a page. Only mounted in
          previous-orders mode, so a normal search keeps the adapter's own
          per_page and no filter. */}
      {isPreviousOrders && previousOrdersFilter && (
        <Configure
          // A raw Typesense `filter_by` clause, NOT `facetFilters` - see
          // ./previous-orders for why the latter loses the id filter whenever a
          // sidebar refinement is active. The adapter ANDs this with the
          // widget-derived clause, so the filters still compose.
          //
          // `filters` types as absent here: `PlainSearchParameters` inherits it
          // from algoliasearch's `SearchOptions`, which resolves to nothing
          // because this app talks to Typesense and has no algoliasearch
          // dependency to resolve it against. The cast restores a parameter the
          // widget genuinely accepts rather than papering over a real type
          // error - `hitsPerPage`, declared on the helper itself, needs no help.
          {...({
            filters: previousOrdersFilter,
            hitsPerPage: previousOrderIds.length
          } as ComponentProps<typeof Configure>)}
        />
      )}
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="border-b bg-white px-6 py-4 dark:border-gray-800 dark:bg-black">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Heading level="h1" className="text-2xl font-semibold">
                {isPreviousOrders ? "Previous orders" : "Search Results"}
              </Heading>
              {isPreviousOrders && (
                <>
                  <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                    <span>Showing results for "Previous orders"</span>
                    <button
                      // Mirrors the free-text "Clear search query" button below,
                      // down to the wording: same shape, same placement, same
                      // affordance - so leaving this mode isn't a different
                      // interaction from leaving a text search. Distinct
                      // accessible name from the sidebar's "Clear" (which clears
                      // the facet filters but deliberately keeps this mode - see
                      // clearUrlFilters). This is the only control that actually
                      // leaves previous-orders mode.
                      aria-label="Clear previous orders filter"
                      onClick={() =>
                        navigate({
                          to: "/search",
                          search: (prev) => ({
                            ...prev,
                            previous_orders: undefined
                          }),
                          replace: true
                        })
                      }
                      className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </button>
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Products you ordered in the last{" "}
                    {PREVIOUS_ORDERS_WINDOW_DAYS} days, most recent first
                  </p>
                </>
              )}
              {q && (
                <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                  <span>
                    Showing results for "{q}"
                    {type && (
                      <Badge className="ml-2" size="small">
                        {type}
                      </Badge>
                    )}
                  </span>
                  <button
                    // Distinguished from the sidebar's "Clear" (which clears the
                    // filters): two buttons with the same accessible name doing
                    // different things is ambiguous to a screen reader.
                    aria-label="Clear search query"
                    onClick={() => {
                      navigate({
                        to: "/search",
                        search: (prev) => ({
                          // Clear the query, keep everything else. Spreading
                          // `prev` rather than re-listing the keys to keep: the
                          // hand-maintained list silently dropped every param it
                          // wasn't updated for - `previous_orders` (which would
                          // drop the salon out of the mode), and the price/stock/
                          // sale filters before it.
                          ...prev,
                          q: undefined,
                          type: undefined
                        }),
                        replace: true
                      });
                      refineSearch("");
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </button>
                </p>
              )}
              {/* Suppressed whenever the grid below is suppressed: the count
                would otherwise be the whole catalogue's, contradicting it. */}
              {results &&
                !previousOrdersLoading &&
                !previousOrdersEmpty &&
                !previousOrdersFailed && (
                  <p className="mt-1 text-sm text-gray-500">
                    {results.nbHits} products found
                  </p>
                )}
            </div>
            <div className="flex h-9 items-stretch gap-3">
              <SortByDropdown />
              {/* Below lg: open the off-canvas filters drawer, with the applied
                  count so filter state is legible before it's opened. */}
              <Button
                variant="secondary"
                size="small"
                className="h-9 lg:hidden"
                onClick={() => setDrawerOpen(true)}
              >
                <Filter className="h-4 w-4" />
                Filters{appliedCount > 0 ? ` (${appliedCount})` : ""}
              </Button>
            </div>
          </div>
        </div>

        {/* Main content. Below lg the filters move into the drawer, so the row
            is just the results (and the cart rail, which is desktop-only). */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 py-6 lg:flex-row">
          {/* Desktop sidebar. Always mounted - never conditionally rendered -
              so its InstantSearch widgets keep the facets declared and the
              refinements applied even while it's hidden (below lg, or when the
              user hides it). Unmounting them would drop the refinements from the
              query and silently unfilter the results - the URL would keep them,
              but the query wouldn't. `hidden` (display:none) keeps it mounted;
              the phone/tablet drawer reuses the same `filterBody`.

              `sticky` + `self-start` (not `lg:h-full`, which stretched it to the
              row's height and left it nowhere to move) pins it in the viewport
              as the row's shared `overflow-y-auto` ancestor scrolls - the same
              pattern the cart rail below already uses.

              `max-h` is sized off the row (`100%`), not the viewport
              (`100vh`) - the row is exactly the viewport minus this header,
              and that header's height isn't constant (the previous-orders
              mode adds a second description line). A `100vh`-based cap sized
              for the shortest header is too tall once a taller header shrinks
              the row: sticky then has no room left for both the `top` offset
              and the full max-height, and silently drops the offset instead,
              pinning the panel's bottom to the row's edge and pushing "Filters"
              off the top under the header. Subtracting the same offset from
              `100%` keeps the two in sync however tall the header gets. */}
          <aside
            className={cn(
              "subtle-scrollbar hidden flex-shrink-0 overflow-y-auto bg-gray-50 p-4 lg:sticky lg:top-0 lg:h-fit lg:max-h-[calc(100%-1.5rem)] lg:w-64 lg:self-start lg:border-r dark:border-gray-800 dark:bg-zinc-950",
              showFilters && "lg:block"
            )}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold">Filters</h3>
              <div className="flex items-center gap-1">
                {appliedCount > 0 && (
                  <Button size="small" variant="transparent" onClick={clearAll}>
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                )}
                {/* Desktop: hide the persistent sidebar, from the sidebar
                    itself. `Show Filters` below brings it back. */}
                <Button
                  size="small"
                  variant="transparent"
                  aria-label="Hide Filters"
                  title="Hide filters"
                  onClick={() => setShowFilters(false)}
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {filterBody}
          </aside>

          {/* Product Grid */}
          <main className="min-w-0 flex-1">
            {/* Desktop: restores the hidden sidebar, at the edge it left from. */}
            {!showFilters && (
              <Button
                variant="secondary"
                size="small"
                className="mb-4 hidden lg:inline-flex"
                onClick={() => setShowFilters(true)}
              >
                <PanelLeftOpen className="h-4 w-4" />
                Show Filters
              </Button>
            )}
            {/* Applied-filter chips, at every breakpoint - so filter state is
                visible and each facet removable without opening the panel (the
                phone case, where the panel is the drawer). Renders nothing when
                nothing is applied. */}
            <AppliedFilterChips
              chips={chips}
              onClearAll={clearAll}
              className="mb-4"
            />
            {previousOrdersFailed ? (
              // Distinct from the empty state on purpose. "No products found" is
              // a claim about this salon's own purchase history, and it would be
              // a false one - we simply could not load it.
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-lg text-gray-500">
                    Couldn't load your previous orders
                  </p>
                  <p className="mt-2 text-sm text-gray-400">
                    Something went wrong on our side.
                  </p>
                  <Button
                    size="small"
                    variant="secondary"
                    className="mt-4"
                    onClick={() => refetchPreviousOrders()}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            ) : previousOrdersLoading ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-gray-400">
                  Loading your previous orders...
                </p>
              </div>
            ) : previousOrdersEmpty || displayItems.length === 0 ? (
              // Deliberately the same zero-result state as any other search: a
              // salon whose past products are all discontinued sees the standard
              // empty state, not a special dead end (KAN-51).
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-lg text-gray-500">No products found</p>
                  <p className="mt-2 text-sm text-gray-400">
                    Try adjusting your filters or search query
                  </p>
                </div>
              </div>
            ) : (
              <>
                <SearchResults
                  items={displayItems}
                  selectedSuppliers={suppliers}
                  searchQuery={q}
                />

                <PaginationControls />
              </>
            )}
          </main>

          {/* Cart Sidebar */}
          <CartSidebar className="sticky top-0 flex-shrink-0 self-start" />
        </div>

        {/* Phone / tablet filters drawer. Only rendered below lg - so it never
            mounts a second copy of the filters on desktop - and the sticky
            footer keeps `Show N products` (close) and `Clear all` in reach past
            a long facet list. The always-mounted sidebar holds the refinements
            while this is closed, so closing it doesn't unfilter the results. */}
        {!isDesktop && (
          <DrawerPanel
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            title="Filters"
            footer={
              <>
                {appliedCount > 0 && (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear all
                  </Button>
                )}
                <Button onClick={() => setDrawerOpen(false)}>
                  Show {results?.nbHits ?? 0} products
                </Button>
              </>
            }
          >
            {filterBody}
          </DrawerPanel>
        )}
      </div>
    </ProductDetailDialogProvider>
  );
}

function OnSaleFilter({
  urlValue,
  onFilterChange
}: {
  urlValue?: boolean;
  onFilterChange?: (value: boolean) => void;
}) {
  // `satisfies` ties this literal to the facet table: renaming or removing the
  // `on_sale` entry from FACETS makes this a compile error rather than a drift.
  const { value, refine } = useToggleRefinement({
    attribute: "on_sale" satisfies FacetAttribute
  });

  useEffect(() => {
    if (urlValue && !value.isRefined) {
      refine(value);
    }
  }, [urlValue]);

  const handleChange = () => {
    refine(value);
    onFilterChange?.(!value.isRefined);
  };

  return (
    <div className="mb-6">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={value.isRefined}
          onChange={handleChange}
          className="h-4 w-4 rounded border-gray-300"
        />
        On sale only
      </label>
    </div>
  );
}

function InStockFilter({
  urlValue,
  onFilterChange
}: {
  urlValue?: boolean;
  onFilterChange?: (value: boolean) => void;
}) {
  // See OnSaleFilter: `satisfies` keeps this attribute tied to the FACETS table.
  const { value, refine } = useToggleRefinement({
    attribute: "in_stock" satisfies FacetAttribute
  });

  useEffect(() => {
    if (urlValue && !value.isRefined) {
      refine(value);
    }
  }, [urlValue]);

  const handleChange = () => {
    refine(value);
    onFilterChange?.(!value.isRefined);
  };

  return (
    <div className="mb-6">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={value.isRefined}
          onChange={handleChange}
          className="h-4 w-4 rounded border-gray-300"
        />
        In stock only
      </label>
    </div>
  );
}

function FilterSection({
  title,
  attribute,
  initialValue,
  urlValues,
  onFilterChange
}: {
  title: string;
  // Constrained to the list-kind facets this component is built for (it renders
  // a `useRefinementList`). Derived from the FACETS table, so a new list filter
  // wired up with an attribute the table doesn't carry as a list facet - unlisted
  // entirely, or a toggle/range attribute - is a compile error here, not silent
  // drift (no URL persistence, a chip that can't be removed).
  attribute: ListFacetAttribute;
  initialValue?: string;
  urlValues?: string[];
  onFilterChange?: (attribute: string, values: string[]) => void;
}) {
  // Fetch the full facet list (not just the top-N by count) so the
  // alphabetical truncation below isn't skewing towards high-count values -
  // e.g. a low-volume brand like "B3" was being dropped entirely because
  // InstantSearch's own limit/showMore truncate by count before we get a
  // chance to sort.
  const { items: unsortedItems, refine } = useRefinementList({
    attribute,
    limit: 1000
  });
  const sortedItems = useMemo(
    () => [...unsortedItems].sort((a, b) => a.label.localeCompare(b.label)),
    [unsortedItems]
  );
  const [isShowingMore, setIsShowingMore] = useState(false);
  const items = isShowingMore ? sortedItems : sortedItems.slice(0, 10);
  const canToggleShowMore = sortedItems.length > 10;
  const [isExpanded, setIsExpanded] = useState(true);
  const [initialApplied, setInitialApplied] = useState(false);
  const [urlSynced, setUrlSynced] = useState(false);

  // Apply initial filter value from type param (e.g., ?type=brand&q=Dermacol)
  useEffect(() => {
    if (initialApplied || !initialValue) return;

    const match = sortedItems.find(
      (item) => item.value.toLowerCase() === initialValue.toLowerCase()
    );
    if (match && !match.isRefined) {
      refine(match.value);
      setInitialApplied(true);
    }
  }, [initialValue, sortedItems, refine, initialApplied]);

  // Apply filters from URL params (e.g., ?brands=Dermacol&brands=HAAN)
  useEffect(() => {
    if (urlSynced || !urlValues || urlValues.length === 0) return;
    if (sortedItems.length === 0) return;

    // Apply each URL value that isn't already refined
    for (const urlValue of urlValues) {
      const match = sortedItems.find(
        (item) => item.value.toLowerCase() === urlValue.toLowerCase()
      );
      if (match && !match.isRefined) {
        refine(match.value);
      }
    }
    setUrlSynced(true);
  }, [urlValues, sortedItems, refine, urlSynced]);

  // Reset sync state when URL values change
  useEffect(() => {
    setUrlSynced(false);
  }, [urlValues]);

  // Reset when initialValue changes
  useEffect(() => {
    setInitialApplied(false);
  }, [initialValue]);

  // Handle checkbox change - update both InstantSearch and URL
  const handleRefine = (value: string) => {
    refine(value);

    // Calculate new refined values after this toggle
    const currentRefined = sortedItems
      .filter((item) => item.isRefined)
      .map((item) => item.value);

    const isCurrentlyRefined = currentRefined.includes(value);
    const newRefined = isCurrentlyRefined
      ? currentRefined.filter((v) => v !== value)
      : [...currentRefined, value];

    // Update URL
    onFilterChange?.(attribute, newRefined);
  };

  if (sortedItems.length === 0) return null;

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-2 flex w-full items-center justify-between text-sm font-medium"
      >
        {title}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      {isExpanded && (
        <div className="space-y-2">
          {items.map((item) => (
            <label
              key={item.value}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => handleRefine(item.value)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="flex-1">{item.label}</span>
              <span className="text-xs text-gray-400">({item.count})</span>
            </label>
          ))}
          {canToggleShowMore && (
            <button
              onClick={() => setIsShowingMore(!isShowingMore)}
              className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {isShowingMore ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PriceRangeFilter({
  urlMinPrice,
  urlMaxPrice,
  onPriceChange
}: {
  urlMinPrice?: number;
  urlMaxPrice?: number;
  onPriceChange?: (min: number | undefined, max: number | undefined) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  // `filterBody` mounts twice at once below lg (the hidden sidebar copy plus the
  // open drawer copy), so the input ids must be unique per instance - otherwise
  // both `<label for>`s resolve to the first `#min-price` in the document (the
  // hidden sidebar's), and tapping the drawer's label focuses nothing.
  const fieldId = useId();
  const minId = `${fieldId}-min`;
  const maxId = `${fieldId}-max`;

  // Use range hook for min_price filtering. `satisfies` keeps this attribute
  // tied to the FACETS table (see OnSaleFilter).
  const { range, refine, canRefine } = useRange({
    attribute: "meta.min_price" satisfies FacetAttribute
  });

  // Local state for input values
  const [minInput, setMinInput] = useState(
    urlMinPrice !== undefined ? String(urlMinPrice) : ""
  );
  const [maxInput, setMaxInput] = useState(
    urlMaxPrice !== undefined ? String(urlMaxPrice) : ""
  );

  // Sync input values from URL params
  useEffect(() => {
    setMinInput(urlMinPrice !== undefined ? String(urlMinPrice) : "");
    setMaxInput(urlMaxPrice !== undefined ? String(urlMaxPrice) : "");
  }, [urlMinPrice, urlMaxPrice]);

  // Apply URL values to InstantSearch on mount and when URL changes
  useEffect(() => {
    if (urlMinPrice !== undefined || urlMaxPrice !== undefined) {
      refine([urlMinPrice, urlMaxPrice]);
    } else {
      // Clear the refinement if no URL params
      refine([undefined, undefined]);
    }
  }, [urlMinPrice, urlMaxPrice, refine]);

  const handleApply = () => {
    const minValue = minInput ? Number(minInput) : undefined;
    const maxValue = maxInput ? Number(maxInput) : undefined;
    refine([minValue, maxValue]);
    onPriceChange?.(minValue, maxValue);
  };

  const handleClear = () => {
    setMinInput("");
    setMaxInput("");
    refine([undefined, undefined]);
    onPriceChange?.(undefined, undefined);
  };

  // Handle Enter key in inputs
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  // Commit a typed value when focus leaves the price group. Without this,
  // tapping the drawer's prominent "Show N products" button (which closes the
  // drawer) - the natural mobile gesture - would silently discard a price the
  // buyer typed but didn't explicitly "Apply".
  //
  // Only when focus leaves the group entirely: moving from Min to Max (or to the
  // Apply button) keeps focus inside `priceGroupRef`, so we don't fire a
  // premature `[min, undefined]` search mid-entry, and clicking Apply doesn't
  // double-commit (its own blur is skipped, then its onClick applies once). And
  // only when the typed value actually differs from what's applied, so leaving
  // an untouched field doesn't re-run the search.
  const priceGroupRef = useRef<HTMLDivElement>(null);
  const handleBlur = (e: React.FocusEvent) => {
    if (
      e.relatedTarget instanceof Node &&
      priceGroupRef.current?.contains(e.relatedTarget)
    ) {
      return;
    }
    const minValue = minInput ? Number(minInput) : undefined;
    const maxValue = maxInput ? Number(maxInput) : undefined;
    if (minValue !== urlMinPrice || maxValue !== urlMaxPrice) {
      handleApply();
    }
  };

  if (!canRefine) return null;

  // Use URL params as source of truth for active filter state
  const hasActiveFilter =
    urlMinPrice !== undefined || urlMaxPrice !== undefined;

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-2 flex w-full items-center justify-between text-sm font-medium"
      >
        Price Range
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      {isExpanded && (
        <div className="space-y-2" ref={priceGroupRef}>
          {range.min !== undefined && range.max !== undefined && (
            <p className="text-xs text-gray-400">
              Range: ${range.min.toFixed(2)} - ${range.max.toFixed(2)}
            </p>
          )}
          {/* Min/Max side-by-side so the Price Range filter stays compact and
              doesn't push the sidebar off-screen (storefront#82). */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor={minId} className="text-xs">
                Min Price
              </Label>
              <Input
                id={minId}
                type="number"
                placeholder={range.min !== undefined ? `$${range.min}` : "$0"}
                value={minInput}
                min={0}
                onChange={(e) => setMinInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={maxId} className="text-xs">
                Max Price
              </Label>
              <Input
                id={maxId}
                min={0}
                type="number"
                placeholder={
                  range.max !== undefined ? `$${range.max}` : "$1000"
                }
                value={maxInput}
                onChange={(e) => setMaxInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="small"
              variant="secondary"
              className="flex-1"
              onClick={handleApply}
            >
              Apply
            </Button>
            {hasActiveFilter && (
              <Button size="small" variant="transparent" onClick={handleClear}>
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SortByDropdown() {
  const { options, refine, currentRefinement } = useSortBy({
    items: [
      { label: "Relevance", value: "products" },
      // The sortable field is `meta.min_price` (matches the adapter's
      // collectionSpecificSearchParameters and the Typesense schema). Using a
      // bare `min_price` here made Typesense 404 ("Could not find a field named
      // `min_price` in the schema for sorting") and emptied the results. See
      // salonstocker/storefront#99.
      {
        label: "Price: Low to High",
        value: "products/sort/meta.min_price:asc"
      },
      {
        label: "Price: High to Low",
        value: "products/sort/meta.min_price:desc"
      }
    ]
  });

  return (
    <div className="relative h-full">
      <select
        value={currentRefinement}
        onChange={(e) => refine(e.target.value)}
        className="h-9 appearance-none rounded-md border border-gray-300 bg-white pl-4 pr-8 text-sm leading-9 dark:border-gray-700 dark:bg-zinc-900"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <SlidersHorizontal className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    </div>
  );
}

function PaginationControls() {
  const { currentRefinement, nbPages, refine, isFirstPage, isLastPage } =
    usePagination();

  if (nbPages <= 1) return null;

  const pages = Array.from({ length: nbPages }, (_, i) => i);
  const visiblePages = pages.slice(
    Math.max(0, currentRefinement - 2),
    Math.min(nbPages, currentRefinement + 3)
  );

  return (
    <div className="mt-8 flex items-center justify-center gap-2">
      <Button
        size="small"
        variant="secondary"
        onClick={() => refine(currentRefinement - 1)}
        disabled={isFirstPage}
      >
        Previous
      </Button>

      {visiblePages[0] > 0 && (
        <>
          <Button
            size="small"
            variant={currentRefinement === 0 ? "primary" : "secondary"}
            onClick={() => refine(0)}
          >
            1
          </Button>
          {visiblePages[0] > 1 && <span className="px-2">...</span>}
        </>
      )}

      {visiblePages.map((page) => (
        <Button
          key={page}
          size="small"
          variant={currentRefinement === page ? "primary" : "secondary"}
          onClick={() => refine(page)}
        >
          {page + 1}
        </Button>
      ))}

      {visiblePages[visiblePages.length - 1] < nbPages - 1 && (
        <>
          {visiblePages[visiblePages.length - 1] < nbPages - 2 && (
            <span className="px-2">...</span>
          )}
          <Button
            size="small"
            variant={
              currentRefinement === nbPages - 1 ? "primary" : "secondary"
            }
            onClick={() => refine(nbPages - 1)}
          >
            {nbPages}
          </Button>
        </>
      )}

      <Button
        size="small"
        variant="secondary"
        onClick={() => refine(currentRefinement + 1)}
        disabled={isLastPage}
      >
        Next
      </Button>
    </div>
  );
}
